import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { fmtClock, ymdLocal } from '../lib/format.js';
import { locationKeyFromName, isoMondayOf } from '../lib/schedule.js';

// Gantt bars in the day cards (CJ 2026-08-09). The time used to be text on the
// right of each row; the bar goes in the SAME row at the same height, spending
// the empty middle on saying when. No extra height — CJ checked the live screen
// and was right that my first read of this was wrong.
//
// Worth knowing, from MyScheduleView: "R44: Gantt dropped — staff confused,
// managers can use the Build Schedule timeline instead." That was a separate
// full-width gantt MODE. This is different — the row stays a row and keeps the
// name and the times in words — but if staff find it confusing again, that
// history is why.
function minutesOf(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

// Per-day scale rather than a fixed 24h one: a day where everyone works
// 5:30a–6p would otherwise squeeze into half the track and show nothing.
// Padded by 30 min so a bar never starts flush against the edge.
function dayBounds(shifts) {
  let lo = null; let hi = null;
  for (const s of shifts) {
    const a = minutesOf(s.scheduledStart);
    const b = minutesOf(s.scheduledEnd);
    if (a == null || b == null) continue;
    lo = lo == null ? a : Math.min(lo, a);
    hi = hi == null ? b : Math.max(hi, b);
  }
  if (lo == null || hi == null || hi <= lo) return [5 * 60 + 30, 19 * 60];
  return [Math.max(0, lo - 30), Math.min(24 * 60, hi + 30)];
}

// "Mon 18 Aug – Sun 24 Aug 2026" for the printed sheet. Built from the ISO
// Monday with UTC-noon anchoring, the same convention the rest of this file
// uses, so a printed week can never disagree with the grid above it.
function weekRangeLabel(weekStart) {
  const a = new Date(weekStart + 'T12:00:00Z');
  const b = new Date(a); b.setUTCDate(b.getUTCDate() + 6);
  const f = (d, withYear) => d.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
  });
  return `${f(a, false)} – ${f(b, true)}`;
}

function ShiftBar({ start, end, lo, hi, color }) {
  const a = minutesOf(start);
  const b = minutesOf(end);
  const span = hi - lo;
  // A shift with no times (they exist — the live screen showed one rendering
  // as "–") gets an empty track, not a bar pinned at zero width on the left,
  // which would read as "works at 5:30am for no time".
  if (a == null || b == null || span <= 0) {
    return <div style={{ flex: 1, minWidth: 40, height: 8 }} />;
  }
  const left = Math.max(0, ((a - lo) / span) * 100);
  const width = Math.max(2, ((Math.min(b, hi) - Math.max(a, lo)) / span) * 100);
  return (
    <div style={{ flex: 1, minWidth: 40, height: 8, borderRadius: 999,
                  background: 'rgba(0,0,0,0.06)', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, bottom: 0,
                    left: `${left}%`, width: `${width}%`,
                    background: color, opacity: 0.65, borderRadius: 999 }} />
    </div>
  );
}

// ─── Week-grid view (Mon-Sun × scheduled staff) ──────────────────────────────
export function WeekGridView({ location, isManager = false }) {
  const [weekStart, setWeekStart] = useState(() => isoMondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const locationKey = locationKeyFromName(location?.name);

  const load = useCallback(async () => {
    if (!locationKey) return;
    setLoading(true);
    try {
      const res = await api('GET', `/api/schedule/${locationKey}/week?week_start=${weekStart}`);
      setData(res); setError(null);
    } catch (e) { setError(e.message || 'Failed to load week grid'); }
    finally { setLoading(false); }
  }, [locationKey, weekStart]);
  useEffect(() => { load(); }, [load]);

  const thisMon = isoMondayOf(new Date());
  function shiftWeek(weeks) {
    const d = new Date(weekStart + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + weeks * 7);
    setWeekStart(ymdLocal(d));
  }

  // Pivot: rows = staff who have ≥1 shift this week, columns = Mon..Sun.
  const days = data?.days || [];
  const dayHeaders = days.map((d) => {
    const date = new Date(d.date + 'T12:00:00Z');
    return {
      ymd: d.date,
      label: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][date.getUTCDay()],
      mmdd: d.date.slice(5),
    };
  });
  // Build staff → { ymd → shift[] } map
  const staffMap = new Map();
  for (const d of days) {
    for (const s of d.shifts || []) {
      const key = s.teamMemberId || s.employeeName;   // openings keyed by name fallback
      if (!staffMap.has(key)) {
        staffMap.set(key, { name: s.employeeName, role: s.jobTitle, byDay: {} });
      }
      const row = staffMap.get(key);
      (row.byDay[d.date] = row.byDay[d.date] || []).push(s);
    }
  }
  const staffRows = Array.from(staffMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  // Per-column bounds for the desktop matrix, computed once rather than per
  // cell — every bar in a column must share a scale or they cannot be compared,
  // which is the entire point of drawing them.
  const colBounds = {};
  for (const d of days) colBounds[d.date] = dayBounds(d.shifts || []);
  const todayYmd = ymdLocal(new Date());

  // Round 32: scroll today's day-card into view on first data load.
  useEffect(() => {
    if (!data || weekStart !== thisMon) return;
    setTimeout(() => document.getElementById(`grid-day-${todayYmd}`)?.scrollIntoView({ behavior: 'auto', block: 'start' }), 50);
  }, [data?.days?.length === 7]);

  return (
    <div className="space-y-3">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(-1)}>← Prev</button>
        <button onClick={() => {
          setWeekStart(thisMon);
          setTimeout(() => document.getElementById(`grid-day-${todayYmd}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        }} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700,
                     background: 'var(--gold)', color: '#fff', border: 'none',
                     borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit' }}>
          Today
        </button>
        <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(1)}>Next →</button>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginLeft: 4 }}>
          Wk of {new Date(weekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
        <span style={{ fontSize: 11, color: '#9e9e9e', marginLeft: 'auto' }}>
          {staffRows.length} staff
        </span>
        {/* CJ 2026-08-18: print the week for the café currently selected.
            window.print() rather than a server-rendered PDF — the browser's
            own "Save as PDF" gives a file when one is wanted and paper when
            it isn't, and the sheet stays in step with the screen instead of
            becoming a second layout to keep true. */}
        <button className="btn-secondary no-print" onClick={() => window.print()}
                disabled={!data || staffRows.length === 0}
                title={staffRows.length === 0
                  ? 'Nothing scheduled this week to print'
                  : `Print the ${location?.name || ''} schedule for this week`}
                style={{ padding: '6px 12px' }}>
          Print
        </button>
      </div>

      {loading && !data && <div className="card text-sm text-gray-500">Loading grid…</div>}
      {error && <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm font-semibold text-red-700">Schedule unavailable</div>
        <div className="text-xs text-gray-600 mt-1">{error}</div>
      </div>}

      {data && staffRows.length === 0 && (
        <div className="card text-sm text-gray-500">No shifts scheduled for this week.</div>
      )}

      {/* Mobile: stack as one card per day with the shifts inside.
          Desktop: classic Mon–Sun × staff matrix. */}
      {data && staffRows.length > 0 && (
        <div className="grid-mobile-cards" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dayHeaders.map((h) => {
            // Sorted by start time (CJ 2026-08-09): opens at the top of each day,
            // closers at the bottom. Alphabetical said nothing about the shape of
            // the day, which is the whole reason for drawing the bars. Shifts
            // with no start time sink to the bottom rather than sorting as 00:00
            // and claiming the top of the list.
            const dayShifts = staffRows
              .flatMap((row) => (row.byDay[h.ymd] || []).map((s) => ({ ...s, _name: row.name, _role: row.role })))
              .sort((a, b) => (minutesOf(a.scheduledStart) ?? 1e9) - (minutesOf(b.scheduledStart) ?? 1e9)
                           || (a._name || '').localeCompare(b._name || ''));
            const [dayLo, dayHi] = dayBounds(dayShifts);
            const isToday = h.ymd === ymdLocal(new Date());
            return (
              <div key={h.ymd} id={`grid-day-${h.ymd}`} className="card"
                style={{ padding: 12, scrollMarginTop: 80,
                          border: isToday ? '2px solid var(--gold)' : undefined }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{h.label}</span>
                    <span style={{ fontSize: 12, color: '#9e9e9e', marginLeft: 6 }}>{h.mmdd}</span>
                    {isToday && <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#e8f5e9', color: '#2e7d32', textTransform: 'uppercase' }}>Today</span>}
                  </div>
                  <span style={{ fontSize: 11, color: '#9e9e9e' }}>{dayShifts.length} shift{dayShifts.length === 1 ? '' : 's'}</span>
                </div>
                {dayShifts.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#cfd8dc', fontStyle: 'italic' }}>No shifts</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {dayShifts.map((s, i) => {
                      // Per-day scale, not a fixed 24h one: a day where everyone
                      // works 5:30a–6p would otherwise squeeze into a third of
                      // the track and show nothing useful.
                      const tone = !isManager
                        ? { bg: 'var(--cream-soft)', text: '#37474f' }
                        : s.clockStatus === 'not_clocked_in' ? { bg: '#ffebee', text: '#c62828' }
                        : s.clockStatus === 'late' ? { bg: '#fff8e1', text: '#a16207' }
                        : s.clockStatus === 'on_time' || s.clockStatus === 'clocked_out' ? { bg: '#e8f5e9', text: '#2e7d32' }
                        : { bg: 'var(--cream-soft)', text: '#37474f' };
                      return (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                padding: '6px 10px', borderRadius: 6, background: tone.bg, gap: 8 }}>
                          <div style={{ minWidth: 96, maxWidth: 140, flexShrink: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{s._name}</div>
                            {s._role && <div style={{ fontSize: 10, color: '#757575' }}>{s._role}</div>}
                          </div>
                          {/* CJ 2026-08-09: the time used to be text on the right. The bar
                              sits in the SAME row at the same height — it just spends the
                              empty middle of the row saying when, so overlap across the
                              day is visible without reading every line. */}
                          <ShiftBar start={s.scheduledStart} end={s.scheduledEnd} lo={dayLo} hi={dayHi} color={tone.text} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: tone.text, whiteSpace: 'nowrap' }}>
                            {fmtClock(s.scheduledStart)}–{fmtClock(s.scheduledEnd)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {data && staffRows.length > 0 && (
        <div className="print-area">
        {/* Only visible on paper. A printed grid with no café name and no week
            on it is a sheet of names — and these get posted next to each other
            in the back of house, where four cafés' schedules look identical. */}
        <div className="print-only print-header" style={{ marginBottom: 10 }}>
          <div style={{ fontSize: '15pt', fontWeight: 800 }}>
            {location?.name || 'Schedule'} — weekly schedule
          </div>
          <div style={{ fontSize: '10pt', marginTop: 2 }}>
            {weekRangeLabel(weekStart)} · {staffRows.length} staff
            · printed {new Date().toLocaleDateString('en-US',
                { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <div className="grid-desktop-table card" style={{ padding: 0, overflowX: 'auto' }}>
          {/* Desktop / wide-screen grid table */}
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ position: 'sticky', left: 0, background: '#f5f5f5',
                             padding: '8px 12px', textAlign: 'left', fontSize: 11,
                             color: '#757575', textTransform: 'uppercase', letterSpacing: '0.6px',
                             minWidth: 140 }}>
                  Staff
                </th>
                {dayHeaders.map((h) => (
                  <th key={h.ymd} style={{ padding: '8px 6px', minWidth: 100,
                                          textAlign: 'left', fontSize: 11,
                                          color: '#757575', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    {h.label} <span style={{ color: '#bdbdbd', fontWeight: 400 }}>{h.mmdd}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staffRows.map((row) => (
                <tr key={row.name} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={{ position: 'sticky', left: 0, background: '#fff',
                              padding: '8px 12px', fontSize: 13, color: '#212121',
                              fontWeight: 500, minWidth: 140 }}>
                    {row.name}
                    {row.role && (
                      <div style={{ fontSize: 10, color: '#9e9e9e', fontWeight: 400 }}>{row.role}</div>
                    )}
                  </td>
                  {dayHeaders.map((h) => {
                    const cellShifts = row.byDay[h.ymd] || [];
                    return (
                      <td key={h.ymd} style={{ padding: '6px 6px', verticalAlign: 'top' }}>
                        {cellShifts.length === 0 ? (
                          <span style={{ color: '#cfd8dc', fontSize: 11 }}>—</span>
                        ) : cellShifts.map((s, i) => {
                          // Round 30: staff just see "scheduled". Clock-in/out coloring
                          // is for managers only — staff shouldn't see whether someone
                          // else clocked in late.
                          const tone = !isManager
                            ? { bg: '#eceff1', text: '#37474f', dot: '#90a4ae' }
                            : s.clockStatus === 'not_clocked_in' ? { bg: '#ffebee', text: '#c62828', dot: '#c62828' }
                            : s.clockStatus === 'late'           ? { bg: '#fff8e1', text: '#a16207', dot: '#f9a825' }
                            : s.clockStatus === 'on_time' || s.clockStatus === 'clocked_out'
                                                                  ? { bg: '#e8f5e9', text: '#2e7d32', dot: '#2e7d32' }
                            : { bg: '#eceff1', text: '#37474f', dot: '#90a4ae' };
                          return (
                            <div key={i} style={{
                              padding: '4px 6px', borderRadius: 4,
                              background: tone.bg, color: tone.text, fontSize: 11,
                              fontWeight: 600, marginBottom: i > 0 ? 4 : 0,
                              whiteSpace: 'nowrap',
                            }}>
                              {fmtClock(s.scheduledStart)}–{fmtClock(s.scheduledEnd)}
                              {/* The same bar in the desktop matrix, on that
                                  column's own scale, so a manager scanning the
                                  week sees the shape of each day rather than
                                  reading 40 time ranges. Same cell, ~5px taller. */}
                              <ShiftBar start={s.scheduledStart} end={s.scheduledEnd}
                                        lo={colBounds[h.ymd][0]} hi={colBounds[h.ymd][1]} color={tone.dot} />
                            </div>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      <div className="card no-print" style={{ fontSize: 11, color: '#757575', padding: 10 }}>
        Staff with no shifts this week are hidden. Published from Monarch.
      </div>
    </div>
  );
}
